/**
 * CreatorAvatar — deterministic circular avatar for a creator.
 *
 * Usage:
 *   <CreatorAvatar handle="@creatorA" size={28} src={url?} />
 *
 * Behavior:
 *   • If `src` is a truthy URL, renders the image (with fallback on error)
 *   • Otherwise renders a gradient circle with the first 1–2 letters of the
 *     handle. Gradient is deterministic from the handle so each creator keeps
 *     the same color across the app.
 */
import React, { useState, useMemo } from 'react';

const PALETTES = [
  ['#00D4FF', '#0891B2'],
  ['#FF4D94', '#BE185D'],
  ['#4ADE80', '#15803D'],
  ['#FACC15', '#B45309'],
  ['#A78BFA', '#6D28D9'],
  ['#F472B6', '#9D174D'],
  ['#60A5FA', '#1D4ED8'],
  ['#FB923C', '#9A3412'],
  ['#22D3EE', '#0E7490'],
  ['#F87171', '#B91C1C'],
];

// Deterministic artsy portrait from a public CDN — seeded by handle so the
// same creator always renders the same face across every view in the app.
// `lorelei` gives us a premium, hand-drawn, brand-safe aesthetic.
function dicebearUrl(handle) {
  const seed = encodeURIComponent((handle || 'x').replace(/^@/, ''));
  return `https://api.dicebear.com/7.x/lorelei/svg?seed=${seed}&backgroundColor=0a0a0a,0f0f0f,121212&radius=50`;
}

function hashCode(str) {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h << 5) - h + str.charCodeAt(i);
    h |= 0;
  }
  return Math.abs(h);
}

function cleanHandle(h = '') {
  return h.replace(/^@/, '').trim();
}

function initialsOf(handle = '') {
  const c = cleanHandle(handle);
  if (!c) return '?';
  const letters = c.replace(/[^a-zA-Z0-9]/g, '');
  if (letters.length <= 1) return letters.toUpperCase() || '?';
  // Prefer first + first-capital-after-first for camelcase handles
  const caps = letters.slice(1).match(/[A-Z]/);
  if (caps) return (letters[0] + caps[0]).toUpperCase();
  return (letters[0] + letters[1]).toUpperCase();
}

export default function CreatorAvatar({ handle, src, size = 28, ring, ringColor, title }) {
  const [failed, setFailed] = useState(false);
  const palette = useMemo(() => PALETTES[hashCode(cleanHandle(handle) || 'x') % PALETTES.length], [handle]);
  const initials = useMemo(() => initialsOf(handle), [handle]);
  const s = typeof size === 'number' ? `${size}px` : size;
  const fs = Math.max(9, Math.round((typeof size === 'number' ? size : 28) * 0.38));
  const border = ring ? `2px solid ${ringColor || palette[0]}` : '1px solid #1f1f1f';

  // Priority: explicit src → deterministic dicebear avatar → gradient initials
  const imgUrl = !failed ? (src || dicebearUrl(handle)) : null;
  const showImg = !!imgUrl;
  return (
    <span
      title={title || handle}
      aria-label={handle}
      className="inline-flex items-center justify-center rounded-full flex-shrink-0 overflow-hidden align-middle"
      style={{
        width: s, height: s,
        background: showImg ? '#0A0A0A' : `linear-gradient(135deg, ${palette[0]}, ${palette[1]})`,
        border,
        boxShadow: ring ? `0 0 10px ${ringColor || palette[0]}55` : 'none',
      }}
    >
      {showImg ? (
        <img
          src={imgUrl}
          alt={handle}
          onError={() => setFailed(true)}
          className="w-full h-full object-cover"
        />
      ) : (
        <span className="font-black text-white leading-none" style={{ fontSize: fs, letterSpacing: '-0.02em' }}>
          {initials}
        </span>
      )}
    </span>
  );
}

export function CreatorAvatarStack({ creators = [], max = 5, size = 24 }) {
  const shown = creators.slice(0, max);
  const overflow = Math.max(0, creators.length - shown.length);
  return (
    <span className="inline-flex items-center" data-testid="creator-avatar-stack">
      {shown.map((c, i) => (
        <span key={c.handle || c.creator_handle || i} style={{ marginLeft: i === 0 ? 0 : -Math.round(size * 0.35) }}>
          <CreatorAvatar
            handle={c.handle || c.creator_handle}
            src={c.avatar || c.creator_avatar}
            size={size}
          />
        </span>
      ))}
      {overflow > 0 && (
        <span
          className="inline-flex items-center justify-center rounded-full text-[9px] font-bold text-gray-300 bg-[#1f1f1f] border border-[#2a2a2a]"
          style={{ width: size, height: size, marginLeft: -Math.round(size * 0.35) }}
        >
          +{overflow}
        </span>
      )}
    </span>
  );
}
